import {AxiosError} from 'axios';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {ActivitiesGroup} from '../../../../../../../backend/interfaces/activities-group';
import {ActivitiesSector} from '../../../../../../../backend/interfaces/activities-sector';
import {useFetchCompleteList} from '../../../../../../../hooks/fetch-all-items';
import {EntityActivitiesRepository} from '../../../../../../../repositories/entity-activities';

export type ActivitiesSectorAndGroups = {
  sector: ActivitiesSector;
  groups?: ActivitiesGroup[];
};

export type FetchActivitiesSectorsAndGroupsOpts = {
  enabled?: boolean;
};

export type FetchActivitiesSectorsAndGroups = {
  items?: ActivitiesSectorAndGroups[];
  /** True if all data was fetched. (Sectors & Groups) */
  isLoading: boolean;
  /** True Sectors were fetched already. (Still fetching Groups) */
  isSectorsLoading: boolean;
  error?: AxiosError;
};

export function useFetchActivitiesSectorsAndGroups(
  opts?: FetchActivitiesSectorsAndGroupsOpts
): FetchActivitiesSectorsAndGroups {
  const {entityId} = useParams<{entityId: string}>();
  const [items, setItems] = useState<ActivitiesSectorAndGroups[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSectorsLoading, setIsSectorsLoading] = useState<boolean>(true);
  const [error, setError] = useState<AxiosError>();

  // Fetch Activities Sectors
  const {items: activitiesSectors} = useFetchCompleteList({
    fetchFunction: () => {
      return EntityActivitiesRepository.instance.getAllSectors(entityId);
    },
    deps: [entityId],
    enabled: opts?.enabled,
  });

  // Fetch Activities Groups for each Activities Sector
  useEffect(() => {
    const fetch = async (sectors: ActivitiesSector[]) => {
      // Set items with sectors only
      setItems(sectors.map((sector) => ({sector})));
      setIsSectorsLoading(false);

      // Fetch groups for each sector
      const sectorsGroupMap = new Map<ActivitiesSector, ActivitiesGroup[]>();

      for (const sector of sectors) {
        const res = await EntityActivitiesRepository.instance.getAllGroups(entityId, sector.id!);
        sectorsGroupMap.set(sector, res);
      }

      // Done fetching groups
      const result = Array.from(sectorsGroupMap.entries()).map(([sector, groups]) => ({
        sector,
        groups,
      }));
      setItems(result);
      setIsLoading(false);
    };

    if (activitiesSectors && activitiesSectors.length > 0) {
      setIsLoading(true);
      setIsSectorsLoading(true);
      setError(undefined);
      fetch(activitiesSectors)
        .catch((e) => {
          console.error('Error fetching groups', e);
          setError(e);
        })
        .finally(() => {
          setIsSectorsLoading(false);
          setIsLoading(false);
        });
    }
  }, [activitiesSectors, entityId]);

  return {
    items,
    isLoading,
    isSectorsLoading,
    error,
  };
}
